<template>
  <RevContainer class="mx-auto max-w-[760px] py-32">
    <section class="flex flex-col gap-32">
      <div v-if="isRenewable" class="space-y-16">
        <header class="flex flex-col gap-8">
          <h1 class="heading-1">{{ i18n(translations.heading) }}</h1>
          <p>
            {{ i18n(translations.generateNewLinkMessage) }}
          </p>
        </header>

        <RevButton
          full-width="adaptive"
          :loading="isLoading"
          variant="primary"
          @click="handleNewLinkGeneration"
        >
          {{ i18n(translations.generateNewLinkCta) }}
        </RevButton>
      </div>

      <div v-if="!isRenewable" class="space-y-16">
        <header class="flex flex-col gap-8">
          <h1 class="heading-1">{{ i18n(translations.heading) }}</h1>
          <p>
            {{ i18n(translations.magicLinkNotRenewable) }}
          </p>
        </header>
      </div>
      <RevDivider />

      <div class="space-y-16">
        <h2 class="heading-2">{{ i18n(translations.changeYourMindTitle) }}</h2>

        <RevInfoBlock
          :icon="IconWarning"
          :title="i18n(translations.infoBlockTitle)"
          variant="warning"
        />

        <p>{{ i18n(translations.changeYourMindMessage) }}</p>
      </div>
    </section>
  </RevContainer>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app'
import { computed, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import type { MagicLinkError } from '../../BouyguesMagicLinkLayout.types'

import translations from './ExpiredError.translations'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()

const isLoading = ref(false)

const props = defineProps<{
  magicLinkId: string
  magicLinkError: NuxtError<MagicLinkError>
}>()

const isRenewable = computed(() => {
  return !!props.magicLinkError.data?.backmarket?.isRenewable
})

async function handleNewLinkGeneration() {
  isLoading.value = true

  try {
    await $httpFetch(
      '/api/mobile-plan/in-store/v1/magic-links/:magicLinkId/renew',
      {
        method: 'POST',
        pathParams: {
          magicLinkId: props.magicLinkId,
        },
      },
    )
    openSuccessToast({
      title: i18n(translations.renewalSuccessToastTitle),
      content: i18n(translations.renewalSuccessToastContent),
    })
  } catch (e) {
    openErrorToast()
  } finally {
    isLoading.value = false
  }
}
</script>
